/**
 * ClickToSignCapture — checkbox + typed initials primitive.
 *
 * Bill 2026-05-10 — Phase 1 (v0.14.17 bundle).
 *
 * Q1 doc-role tier: used for the lowest-formality signing events where no
 * rendered signature is needed — ESIGN consent (recorded via the dedicated
 * EsignConsentGate, but this primitive could back it too), Reg E opt-in,
 * e-statement preference, marketing opt-outs, internal acknowledgements.
 *
 * Plain-language doctrine — no backend names, no field codes. Headline
 * and body copy aim at the customer's reading level, not the developer's.
 */

import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
} from 'react';
import type { SignatureCaptureResult } from '@retail-ops/shared-types';

// ── Props ──────────────────────────────────────────────────────────────────

export interface ClickToSignCaptureProps {
  /** Customer's display name — shown in the attestation copy. */
  signerName: string;
  /** Document the customer is confirming (shown in the headline area). */
  documentTitle: string;
  /** Called when the customer clicks Confirm with both fields valid. */
  onCapture: (result: SignatureCaptureResult) => void;
  /** Called when the customer dismisses the capture. */
  onCancel: () => void;
  /** When true, the controls render disabled (parent is mid-submit). */
  disabled?: boolean;
}

// ── Styles (PCB brand) ─────────────────────────────────────────────────────

const cardS: CSSProperties = {
  background: '#fff',
  color: '#0f172a',
  borderRadius: 10,
  maxWidth: 560,
  width: '100%',
  padding: 24,
  boxShadow: '0 12px 40px rgba(0,0,0,0.18)',
  display: 'flex',
  flexDirection: 'column',
  gap: 16,
  fontFamily: "'Roboto', system-ui, -apple-system, 'Segoe UI', sans-serif",
};

const headingS: CSSProperties = {
  margin: 0,
  fontSize: 18,
  fontWeight: 600,
  color: '#1a406a',
};

const subHeadS: CSSProperties = {
  margin: 0,
  fontSize: 13,
  color: '#475569',
};

const fieldRowS: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
};

const labelS: CSSProperties = {
  fontSize: 13,
  fontWeight: 500,
  color: '#1f2937',
};

const inputS: CSSProperties = {
  padding: '10px 12px',
  border: '1px solid #cbd5e1',
  borderRadius: 6,
  fontSize: 16,
  fontFamily: 'inherit',
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
};

const checkboxRowS: CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: 10,
  padding: '10px 12px',
  background: '#f9fafb',
  border: '1px solid #e5e7eb',
  borderRadius: 6,
};

const checkS: CSSProperties = {
  marginTop: 2,
  width: 18,
  height: 18,
  flex: '0 0 auto',
  accentColor: '#386732',
};

const checkLabelS: CSSProperties = {
  fontSize: 13,
  fontWeight: 500,
  color: '#1f2937',
  lineHeight: 1.4,
};

const footerS: CSSProperties = {
  display: 'flex',
  gap: 10,
  justifyContent: 'flex-end',
  marginTop: 4,
};

const primaryBtnS: CSSProperties = {
  padding: '10px 18px',
  borderRadius: 6,
  border: '1px solid #386732',
  background: '#386732',
  color: '#fff',
  fontSize: 14,
  fontWeight: 600,
  cursor: 'pointer',
};

const disabledBtnS: CSSProperties = {
  ...primaryBtnS,
  background: '#9ca3af',
  border: '1px solid #9ca3af',
  cursor: 'not-allowed',
};

const ghostBtnS: CSSProperties = {
  padding: '10px 18px',
  borderRadius: 6,
  border: '1px solid #cbd5e1',
  background: '#fff',
  color: '#1f2937',
  fontSize: 14,
  fontWeight: 500,
  cursor: 'pointer',
};

// ── Component ──────────────────────────────────────────────────────────────

/** Best-guess initials seed for the input placeholder. */
function suggestInitials(name: string): string {
  return name
    .split(/\s+/)
    .map((part) => part.charAt(0).toUpperCase())
    .join('')
    .slice(0, 4);
}

export function ClickToSignCapture(props: ClickToSignCaptureProps): JSX.Element {
  const { signerName, documentTitle, onCapture, onCancel, disabled } = props;

  // Capture the time the customer first saw the prompt (intent timestamp).
  // Stable across re-renders via a ref so dependency-changes don't reset it.
  const intentTimestampRef = useRef<string>(new Date().toISOString());

  const [initials, setInitials] = useState<string>('');
  const [confirmed, setConfirmed] = useState<boolean>(false);

  // Plain-language attestation text — surface verbatim so the audit page
  // can show the customer the same words they accepted.
  const attestationLabel = useMemo(
    () =>
      `I, ${signerName}, confirm this is my electronic signature for ${documentTitle}.`,
    [signerName, documentTitle]
  );

  const placeholder = useMemo(() => suggestInitials(signerName), [signerName]);

  const isValid = initials.trim().length >= 2 && confirmed;

  function handleConfirm(): void {
    if (!isValid || disabled) return;
    const now = new Date().toISOString();
    const result: SignatureCaptureResult = {
      mode: 'click',
      initials: initials.trim().toUpperCase(),
      attestationTimestamp: now,
      intentTimestamp: intentTimestampRef.current,
      signerViewportSize: `${window.innerWidth}x${window.innerHeight}`,
      signerUserAgent: navigator.userAgent,
    };
    onCapture(result);
  }

  // Keep keyboard-only users happy — Enter inside the initials input submits.
  useEffect(() => {
    function onKey(e: KeyboardEvent): void {
      if (e.key === 'Enter' && isValid && !disabled) {
        handleConfirm();
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isValid, disabled, initials, confirmed]);

  return (
    <div role="dialog" aria-label="Confirm with your initials" style={cardS}>
      <h2 style={headingS}>Confirm with your initials</h2>
      <p style={subHeadS}>
        Type your initials and tick the box to electronically confirm{' '}
        <strong>{documentTitle}</strong>. This has the same legal effect as a
        handwritten signature under federal e-sign law.
      </p>

      <div style={fieldRowS}>
        <label htmlFor="cts-initials" style={labelS}>
          Type your initials
        </label>
        <input
          id="cts-initials"
          type="text"
          autoComplete="off"
          maxLength={6}
          spellCheck={false}
          placeholder={placeholder || 'e.g. BW'}
          style={inputS}
          value={initials}
          disabled={disabled}
          onChange={(e) =>
            setInitials(e.target.value.replace(/[^A-Za-z]/g, '').slice(0, 6))
          }
        />
      </div>

      <div style={checkboxRowS}>
        <input
          type="checkbox"
          id="cts-confirm"
          style={checkS}
          checked={confirmed}
          disabled={disabled}
          onChange={(e) => setConfirmed(e.target.checked)}
        />
        <label htmlFor="cts-confirm" style={checkLabelS}>
          {attestationLabel}
        </label>
      </div>

      <div style={footerS}>
        <button
          type="button"
          style={ghostBtnS}
          onClick={onCancel}
          disabled={disabled}
        >
          Cancel
        </button>
        <button
          type="button"
          style={isValid && !disabled ? primaryBtnS : disabledBtnS}
          disabled={!isValid || disabled}
          onClick={handleConfirm}
        >
          Confirm
        </button>
      </div>
    </div>
  );
}

export default ClickToSignCapture;
